import { useState, useEffect, useRef, useCallback } from "react";
import { useSearchParams } from "react-router";
import { parseQueryParam } from "@/lib/stateUtils";

type ParamType = "string" | "number" | "boolean";

/**
 * Partial URL updates. Use `undefined` for a key to remove it from state and the URL
 * (`Partial<T>` under exactOptionalPropertyTypes does not allow explicit `undefined`).
 */
export type UrlStatePatch<T extends Record<string, any>> = {
  [K in keyof T]?: T[K] | undefined;
};

interface UseUrlStateOptions<T> {
  /**
   * Schema defining the types of each parameter
   */
  schema: Record<keyof T, ParamType>;

  /**
   * Default values for parameters
   */
  defaults?: Partial<T>;

  /**
   * Use replace instead of push for history navigation
   * Prevents polluting browser history
   */
  replace?: boolean;

  /**
   * Debounce delay in milliseconds
   * Useful for search inputs to avoid excessive URL updates
   */
  debounceMs?: number;
}

/**
 * Custom hook that synchronizes state with URL search parameters
 *
 * @example
 * const [state, setState] = useUrlState({
 *   schema: { page: 'number', search: 'string' },
 *   defaults: { page: 1 },
 *   replace: true,
 *   debounceMs: 300,
 * });
 */
export function useUrlState<T extends Record<string, any>>({
  schema,
  defaults = {},
  replace = false,
  debounceMs = 0,
}: UseUrlStateOptions<T>): [
  T,
  (updates: UrlStatePatch<T> | ((prev: T) => UrlStatePatch<T>)) => void,
] {
  const [searchParams, setSearchParams] = useSearchParams();
  const debounceTimerRef = useRef<number | null>(null);
  const defaultsRef = useRef(defaults);
  const schemaRef = useRef(schema);
  const storageKeyRef = useRef(`urlState:${window.location.pathname}`);

  // Parse initial state from URL and SessionStorage
  const parseUrlState = useCallback((): T => {
    const state = { ...defaultsRef.current } as T;

    // First, try to restore from sessionStorage (for navigation persistence)
    try {
      const stored = sessionStorage.getItem(storageKeyRef.current);
      if (stored) {
        const parsed = JSON.parse(stored);
        Object.assign(state, parsed);
      }
    } catch (error) {
      console.warn("Failed to parse stored URL state:", error);
    }

    // Then, override with URL params (URL takes priority for sharing/bookmarking)
    for (const [key, type] of Object.entries(schemaRef.current)) {
      const urlValue = searchParams.get(key);
      const parsed = parseQueryParam(urlValue, type as ParamType);

      if (parsed !== undefined) {
        state[key as keyof T] = parsed as T[keyof T];
      }
    }

    return state;
  }, [searchParams]);

  const [state, setState] = useState<T>(parseUrlState);
  // Ref keeps the latest state synchronously so updateState can read it without
  // stale closures and without calling setSearchParams inside a setState updater
  // (which would trigger a cross-component update during the React render cycle).
  const stateRef = useRef<T>(state);

  // Sync state when URL changes (browser back/forward)
  useEffect(() => {
    const nextState = parseUrlState();
    stateRef.current = nextState;
    setState(nextState);
  }, [parseUrlState]);

  // Update URL when state changes
  const updateState = useCallback(
    (updates: UrlStatePatch<T> | ((prev: T) => UrlStatePatch<T>)) => {
      // Clear any pending debounce
      if (debounceTimerRef.current !== null) {
        clearTimeout(debounceTimerRef.current);
      }

      // Compute next state from the ref — keeps all side effects (sessionStorage,
      // setSearchParams) outside the setState updater to avoid React's
      // "update during render" warning.
      const prevState = stateRef.current;
      const updateObj =
        typeof updates === "function" ? updates(prevState) : updates;
      // Apply partial updates. Keys set to `undefined` remove that field (spread alone cannot clear keys).
      const newState = { ...prevState } as T;
      for (const [key, value] of Object.entries(
        updateObj as Record<string, unknown>,
      )) {
        if (value === undefined) {
          delete (newState as Record<string, unknown>)[key];
        } else {
          (newState as Record<string, unknown>)[key] = value;
        }
      }

      stateRef.current = newState;
      setState(newState);

      // Save to sessionStorage for persistence across navigation
      try {
        sessionStorage.setItem(storageKeyRef.current, JSON.stringify(newState));
      } catch (error) {
        console.error("Failed to save URL state to sessionStorage:", error);
      }

      // Update URL (potentially debounced)
      const updateUrl = () => {
        // Build new search params
        const newSearchParams = new URLSearchParams();

        for (const [key, value] of Object.entries(newState)) {
          // Skip undefined, null, empty strings, and default values
          if (
            value !== undefined &&
            value !== null &&
            value !== "" &&
            value !== defaultsRef.current[key as keyof T]
          ) {
            newSearchParams.set(key, String(value));
          }
        }

        setSearchParams(newSearchParams, { replace });
      };

      if (debounceMs > 0) {
        debounceTimerRef.current = window.setTimeout(updateUrl, debounceMs);
      } else {
        updateUrl();
      }
    },
    [debounceMs, replace, setSearchParams],
  );

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current !== null) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  return [state, updateState];
}
